/**
 * Capa multi-comunidad (lado vendedor).
 *
 * El inventario de pines pertenece a una comunidad: un voucher cargado para el
 * router de una comunidad no sirve en otra. Por eso toda consulta de pines
 * tiene que filtrar por `comunidadId`, y ese id sale del documento del
 * vendedor en `usuarios/{codigo}`.
 *
 * Coleccion    Documento         Campos que importan aqui
 * -----------  ----------------  --------------------------------------------
 * usuarios     {codigo}          name, active, comunidadId
 * comunidades  {comunidadId}     nombre, active
 * pines        {codigo}          codigo, valor, active, pending, comunidadId
 * config       planes            planes: [{ valor, label }]
 */

import { getFirestore, doc, getDoc } from 'firebase/firestore'

/** Planes que existian antes de que fueran configurables. Sirven de respaldo. */
export const PLANES_FALLBACK = [
  { valor: 500, label: 'PIN 1 hora' },
  { valor: 1000, label: 'PIN 3 horas' },
  { valor: 1500, label: 'PIN 12 horas' },
  { valor: 3000, label: 'PIN 24 horas' },
  { valor: 5000, label: 'PIN 2 dias' }
]

function normalizarPlanes (planes) {
  if (!Array.isArray(planes)) return [...PLANES_FALLBACK]
  const limpios = planes
    .map(p => ({ valor: Number(p.valor), label: String(p.label || '').trim() }))
    .filter(p => Number.isFinite(p.valor) && p.valor > 0 && p.label)
  if (!limpios.length) return [...PLANES_FALLBACK]
  const vistos = new Set()
  return limpios
    .filter(p => (vistos.has(p.valor) ? false : vistos.add(p.valor)))
    .sort((a, b) => a.valor - b.valor)
}

/** Planes y precios configurados por el administrador. */
export async function getPlanes () {
  const db = getFirestore()
  try {
    const snap = await getDoc(doc(db, 'config', 'planes'))
    if (snap.exists()) return normalizarPlanes(snap.data().planes)
  } catch (error) {
    console.error('No se pudieron leer los planes:', error)
  }
  return [...PLANES_FALLBACK]
}

/**
 * Valida el codigo de ingreso y devuelve la comunidad a la que pertenece.
 *
 * Devuelve { ok:true, nombre, comunidadId, comunidad } o
 * { ok:false, motivo } con un mensaje listo para mostrar.
 */
export async function autenticarVendedor (codigo) {
  const db = getFirestore()
  const id = String(codigo).trim()

  const snap = await getDoc(doc(db, 'usuarios', id))
  if (!snap.exists()) return { ok: false, motivo: 'Pin incorrecto' }

  const datos = snap.data()
  if (!datos.active) return { ok: false, motivo: 'Tu usuario está bloqueado' }

  if (!datos.comunidadId) {
    return {
      ok: false,
      motivo: 'Tu usuario no tiene comunidad asignada. Pídele al administrador que te asigne una.'
    }
  }

  const comSnap = await getDoc(doc(db, 'comunidades', datos.comunidadId))
  if (!comSnap.exists()) {
    return { ok: false, motivo: 'Tu comunidad ya no existe. Contacta al administrador.' }
  }

  // El spread va primero para que un campo propio no pise el id del documento
  const comunidad = { ...comSnap.data(), id: comSnap.id }
  if (comunidad.active === false) {
    return { ok: false, motivo: `La comunidad ${comunidad.nombre} está archivada.` }
  }

  return {
    ok: true,
    nombre: datos.name || '',
    comunidadId: comunidad.id,
    comunidad
  }
}


/** Datos de una comunidad por su id. */
export async function getComunidad (comunidadId) {
  if (!comunidadId) return null
  const db = getFirestore()
  const snap = await getDoc(doc(db, 'comunidades', comunidadId))
  return snap.exists() ? { ...snap.data(), id: snap.id } : null
}
